import { Chip, Stack } from '@mui/material'

type Props<T extends string | number> = {
  options: { value: T; label: string }[]
  selected: T[]
  onChange: (selected: T[]) => void
  ariaLabel: string
}

/**
 * 複数選択のチップ。曜日・月・日の指定に使う。押すたびに選択を反転させ、並びは options の順に保つ。
 *
 * onChange に渡す値は常に自分の options に含まれる値だけ（selected に他の群の値が混ざっていても、
 * それらは素通りさせず含めない）。呼び出し側が複数の ToggleChips で selected を共有していても
 * （例: 正の日と末日系の 2 群）、追加・解除のどちらでも相手側の値を巻き込んで返さない。
 */
export const ToggleChips = <T extends string | number>({
  options,
  selected,
  onChange,
  ariaLabel,
}: Props<T>) => {
  const toggle = (value: T) => {
    const next = options
      .map(o => o.value)
      .filter(v =>
        v === value ? !selected.includes(value) : selected.includes(v),
      )
    onChange(next)
  }
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={0.75}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map(option => {
        const pressed = selected.includes(option.value)
        return (
          <Chip
            key={String(option.value)}
            label={option.label}
            clickable
            aria-pressed={pressed}
            color={pressed ? 'primary' : 'default'}
            variant={pressed ? 'filled' : 'outlined'}
            onClick={() => toggle(option.value)}
          />
        )
      })}
    </Stack>
  )
}
